/**
 * Centralized query keys factory for type-safe query key management.
 * Each entity has its own namespace with standardized query key patterns.
 *
 * Queries are defined by `queryOptions` factories that consume these keys —
 * shared reads in `lib/query-options.ts`, route-scoped reads in the route's
 * `-lib/query-options.ts`.
 *
 * @example
 * ```typescript
 * // Basic definition
 * const makeQueryKeys = {
 *   users: {
 *     all: () => ['users'] as const,
 *     index: (filters?: Record<string, unknown>) => ['users', 'index', filters] as const,
 *     show: (id: string) => ['users', id] as const
 *   }
 * } as const
 *
 * // Query — factory built on these keys
 * const userQuery = useQuery(userQueryOptions('123'))
 *
 * // Query with filters
 * const filteredUsers = useQuery(
 *   usersListQueryOptions({ status: 'active', role: 'admin' })
 * )
 *
 * // Mutation — manual mutationFn, body typed from Api*Data['body']
 * const updateUser = useMutation({
 *   mutationFn: (body: ApiUsersUpdateMeData['body']) =>
 *     apiUsersUpdateMe({ body }),
 *   onSuccess: () =>
 *     queryClient.invalidateQueries({ queryKey: makeQueryKeys.users.all() })
 * })
 *
 * // Invalidate all user queries (array prefix matching)
 * queryClient.invalidateQueries({ queryKey: makeQueryKeys.users.all() })
 *
 * // Invalidate one filtered list
 * queryClient.invalidateQueries({
 *   queryKey: makeQueryKeys.users.index({ status: 'active' })
 * })
 *
 * // Prefetch / SSR ensure
 * queryClient.ensureQueryData(userQueryOptions('123'))
 * ```
 */
export const makeQueryKeys = {
	auth: {
		session: () => ['auth', 'session'] as const
	},

	admins: {
		all: () => ['admins'] as const,
		me: () => ['admins', 'me'] as const,
		index: (filters?: Record<string, unknown>) =>
			['admins', 'index', filters] as const
	},
	po: {
		correctionsAll: () => ['po', 'corrections'] as const,
		correctionsIndex: (body?: Record<string, unknown>) =>
			['po', 'corrections', 'index', body] as const,
		correctionDetail: (id: string) =>
			['po', 'corrections', 'detail', id] as const,
		correctionFilters: (runId?: string) =>
			['po', 'corrections', 'filters', runId] as const,
		system: () => ['po', 'system'] as const,
		runsAll: () => ['po', 'runs'] as const,
		runsIndex: (body?: Record<string, unknown>) =>
			['po', 'runs', 'index', body] as const,
		runDetail: (runId: string) => ['po', 'runs', 'detail', runId] as const,
		proposalsAll: (runId: string) => ['po', 'proposals', runId] as const,
		proposalsIndex: (runId: string, body?: Record<string, unknown>) =>
			['po', 'proposals', runId, 'index', body] as const,
		proposalAll: () => ['po', 'proposal'] as const,
		proposalDetail: (id: string) => ['po', 'proposal', id] as const,
		proposalHistory: (id: string) =>
			['po', 'proposal', id, 'history'] as const,
		distributors: () => ['po', 'distributors'] as const,
		publishers: () => ['po', 'publishers'] as const,
		exportsAll: () => ['po', 'exports'] as const,
		exportsIndex: (body?: Record<string, unknown>) =>
			['po', 'exports', 'index', body] as const,
		exportAuthors: (runId?: string) =>
			['po', 'exports', 'authors', runId] as const,
		config: () => ['po', 'config'] as const
	}
} as const
